import { Defect } from '@pages/defects/types';
import { UnknownObject } from '@shared/types';
import { MRT_RowData, MRT_TableInstance } from 'material-react-table';
import { getObjectLinkById } from './utilsOld';
import { UI_DEFECTS_PATH } from '@shared/constants/pathsConstants';

export const retryImport = <T>(fn: () => Promise<T>, retries: number = 3, interval: number = 1500): Promise<T> =>
    new Promise<T>((resolve, reject) => {
        fn()
            .then(resolve)
            .catch((error) => {
                if (retries === 0) {
                    reject(error);
                    return;
                }
                setTimeout(() => {
                    retryImport(fn, retries - 1, interval).then(resolve, reject);
                }, interval);
            });
    });

export function deepGet<T extends object, R = unknown>(obj: T, path: string): R | undefined {
    return path.split('.').reduce<any>((acc, key) => acc?.[key], obj);
}

export function getDatesDifferenceFromDateRange(
    fromDate: Date | string,
    toDate: Date | string,
): [number, number, number] {
    const fromDateObject = new Date(fromDate);
    const toDateObject = new Date(toDate);
    const dateDiffMs = toDateObject.getTime() - fromDateObject.getTime();

    const totalMinutes = dateDiffMs / (1000 * 60);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = Math.floor(totalMinutes % 60);

    return [days, hours, minutes];
}

export function getSelectAllCheckboxState(
    table: MRT_TableInstance<MRT_RowData>,
    rowSelection: Record<string, boolean>,
) {
    const currentPageRowIds = table.getRowModel().rows.map((row) => row.id);
    const selectedRowIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
    const allCurrentPageSelected = currentPageRowIds.length > 0 && currentPageRowIds.every((id) => rowSelection[id]);
    const someCurrentPageSelected = currentPageRowIds.some((id) => rowSelection[id]);
    const someOtherPagesSelected = selectedRowIds.some((id) => !currentPageRowIds.includes(id));

    return {
        checked: allCurrentPageSelected,
        indeterminate: (!allCurrentPageSelected && someCurrentPageSelected) || someOtherPagesSelected,
    };
}

export function getObjectLinkByIdSeveralparams(
    columnFilters: Record<string, string | number>,
    addOrigin: boolean,
    objectUIPath: string,
) {
    const base = `${addOrigin ? window.location.origin : ''}/${objectUIPath}`;
    const encodedColumnFilters = encodeURIComponent(
        JSON.stringify(Object.entries(columnFilters).map(([id, value]) => ({ [id]: value }))),
    );
    return `${base}?${!addOrigin ? 'pageIndex=0&pageSize=10&' : ''}columnFilters=${encodedColumnFilters}`;
}

export function getArrayValueFromURLParams<T>(urlParams: UnknownObject, field: string): T[] {
    try {
        const arrayFromUrl = JSON.parse(String(urlParams[field]));

        if (Array.isArray(arrayFromUrl)) {
            return arrayFromUrl;
        }
    } catch (e) {}

    return [];
}

export function safeJsonParse<T>(value: string, fallback: T): T {
    try {
        return JSON.parse(value) as T;
    } catch {
        return fallback;
    }
}

const toCamelCase = (str: string): string => str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

export const keysToCamelCase = <T extends object>(obj: T): T => {
    if (Array.isArray(obj)) {
        return obj.map(keysToCamelCase) as unknown as T;
    }

    if (obj !== null && typeof obj === 'object') {
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [toCamelCase(key), keysToCamelCase(value)]),
        ) as T;
    }

    return obj;
};

export const toSnakeCase = (str: string): string =>
    str
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase()
        .replace(/^_/, '');

export const keysToSnakeCase = <T>(obj: T): T => {
    if (Array.isArray(obj)) {
        return obj.map(keysToSnakeCase) as unknown as T;
    }

    if (obj !== null && typeof obj === 'object' && obj.constructor === Object) {
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [toSnakeCase(key), keysToSnakeCase(value)]),
        ) as T;
    }

    return obj;
};
export function getDefectLinkById(id: Defect['pk'], addOrigin?: string) {
    const filterOption = 'pk';
    return getObjectLinkById(filterOption, id, addOrigin, `${UI_DEFECTS_PATH}/${id}`);
}
